import React, { useState } from 'react';
import {
  Box, Button, CircularProgress, FormControl, InputAdornment, InputLabel, MenuItem, Paper, Select, TextField, Tooltip
} from '@mui/material';
import { FileDownload as DownloadIcon, Search as SearchIcon } from '@mui/icons-material';
import { ScopeChip } from '../../reports/edit/EditCommon';
import { RemainsMeta, SearchMode, SearchQuery } from '../remainsApi';
import { buttonSx } from '../remainsStyles';

interface Props {
  meta: RemainsMeta;
  loading: boolean;
  /** Выгрузка доступна: есть право и результат поиска */
  canExport: boolean;
  exporting: boolean;
  onSearch: (q: SearchQuery) => void;
  onExport: () => void;
}

const MODES: Array<{ value: SearchMode; label: string; placeholder: string }> = [
  { value: 'all', label: 'Везде', placeholder: 'Название, штрихкод, артикул или МНН' },
  { value: 'barcode', label: 'Штрихкод', placeholder: 'Штрихкод упаковки' },
  { value: 'sku', label: 'Артикул', placeholder: 'Артикул (код 1С)' },
  { value: 'mnn', label: 'МНН', placeholder: 'Действующее вещество' },
  { value: 'analogs', label: 'Аналоги по МНН', placeholder: 'Товар, для которого нужны аналоги' }
];

/** Поиск товара: запрос уходит по «Найти» или Enter, поля - черновик до отправки */
const RemainsToolbar: React.FC<Props> = ({ meta, loading, canExport, exporting, onSearch, onExport }) => {
  const [term, setTerm] = useState('');
  const [mode, setMode] = useState<SearchMode>('all');
  const [minQty, setMinQty] = useState('');

  const tooShort = term.trim().length < meta.limits.termMin;
  const mode0 = MODES.find(m => m.value === mode) || MODES[0];
  const submit = () => {
    if (tooShort) return;
    onSearch({ term: term.trim(), mode, minQty: Math.max(0, Number(minQty.replace(',', '.')) || 0) });
  };

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel shrink>Искать</InputLabel>
          <Select notched label="Искать" value={mode} onChange={e => setMode(e.target.value as SearchMode)}>
            {MODES.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
          </Select>
        </FormControl>

        <TextField
          size="small" placeholder={mode0.placeholder} value={term} autoFocus
          onChange={e => setTerm(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') submit(); }}
          inputProps={{ maxLength: 100 }} sx={{ minWidth: 260, flex: '1 1 340px', maxWidth: 520 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment>
          }}
        />

        <Tooltip title="Показывать аптеки, где товара не меньше указанного количества">
          <TextField
            size="small" label="Остаток от" value={minQty} placeholder="любой"
            onChange={e => setMinQty(e.target.value.replace(/[^\d.,]/g, ''))} onKeyDown={e => { if (e.key === 'Enter') submit(); }}
            InputLabelProps={{ shrink: true }} sx={{ width: 120 }}
          />
        </Tooltip>

        <Tooltip title={tooShort ? `Введите не меньше ${meta.limits.termMin} символов` : ''} disableHoverListener={!tooShort}>
          <span>
            <Button
              variant="contained" size="small" onClick={submit} disabled={tooShort}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              sx={{ ...buttonSx, px: 2.5 }}
            >
              Найти
            </Button>
          </span>
        </Tooltip>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <ScopeChip scope={meta.scope} />
          {meta.permissions.export && (
            <Tooltip title={canExport ? 'Остатки по найденным товарам и фильтрам списка аптек в Excel' : 'Сначала найдите товар'}>
              <span>
                <Button
                  variant="outlined" size="small" color="success" onClick={onExport} disabled={!canExport || exporting}
                  startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />} sx={buttonSx}
                >
                  Выгрузка
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default React.memo(RemainsToolbar);
